import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PrintableMarksheet.css';

const PrintableMarksheet = ({ user }) => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [marksheetData, setMarksheetData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [patternLock, setPatternLock] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [showMarksheet, setShowMarksheet] = useState(false);
  const [showBackSide, setShowBackSide] = useState(false);

  const TEACHER_PATTERN = '2580'; // Pattern lock for teachers

  useEffect(() => {
    // Only allow teachers to access this component
    if (!['teacher', 'admin1', 'admin', 'principal', 'teacher_admin_level1'].includes(user?.role)) {
      setError('Access denied. This feature is only available to teachers.');
      return;
    }
    
    fetchStudents();
  }, [user]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users?role=student`);
      setStudents(response.data);
      setFilteredStudents(response.data); // Initially show all students
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (className) => {
    console.log('Class changed to:', className);
    
    // Clear existing data to prevent mixing
    setSelectedClass(className);
    setStudents([]);
    setFilteredStudents([]);
    setSelectedStudent(null);
    setMarksheetData(null);
    setShowMarksheet(false);
    setShowBackSide(false);
    setError('');
    
    // Fetch students for the selected class
    if (className === '') {
      fetchStudents(); // Fetch all students
    } else {
      fetchStudentsByClass(className); // Fetch specific class
    }
  };

  const fetchStudentsByClass = async (className) => {
    try {
      setLoading(true);
      console.log('Fetching students for class:', className);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users?role=student&class=${className}`);
      console.log(`Found ${response.data.length} students in class ${className}`);
      setStudents(response.data);
      setFilteredStudents(response.data);
    } catch (error) {
      console.error('Error fetching students by class:', error);
      setError('Failed to fetch students for selected class');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarksheetData = async (studentId) => {
    try {
      setLoading(true);
      
      // First try the complete marksheet endpoint
      let marksheetResponse;
      try {
        marksheetResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/student-marksheet-complete/${studentId}`);
      } catch (completeError) {
        console.log('Complete marksheet endpoint failed, trying basic endpoint:', completeError.message);
        // Fallback to basic marksheet endpoint
        marksheetResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/marksheet/${studentId}`);
      }
      
      const student = students.find(s => s._id === studentId);
      
      if (!student) {
        throw new Error('Student not found');
      }

      // Try to fetch marksheet template for the student's class
      let templateResponse;
      try {
        templateResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/marksheet-template/${student.class}`);
      } catch (templateError) {
        console.log('Template not found, using default structure:', templateError.message);
        // Create default template structure
        templateResponse = {
          data: {
            className: student.class,
            subjects: [
              { name: 'hindi', displayName: 'Hindi' },
              { name: 'english', displayName: 'English' },
              { name: 'mathematics', displayName: "Math's" },
              { name: 'science', displayName: 'Science' },
              { name: 'socialScience', displayName: 'Social Science' },
              { name: 'drawing', displayName: 'Drawing' },
              { name: 'computer', displayName: 'Computer' },
              { name: 'gk', displayName: 'GK' },
              { name: 'evs', displayName: 'EVS' },
              { name: 'moralScience', displayName: 'Moral Science' }
            ],
            examStructure: {
              term1: { displayName: 'Term 1', maxMarks: 10, enabled: true, order: 1 },
              term2: { displayName: 'Term 2', maxMarks: 10, enabled: true, order: 2 },
              halfYearly: { displayName: 'Half Yearly', maxMarks: 80, enabled: true, order: 3 },
              term3: { displayName: 'Term 3', maxMarks: 10, enabled: true, order: 4 },
              project: { displayName: 'Project', maxMarks: 10, enabled: true, order: 5 },
              annualExam: { displayName: 'Annual Exam', maxMarks: 80, enabled: true, order: 6 }
            }
          }
        };
      }
      
      // Fetch attendance data for the student
      let attendanceData = null;
      try {
        // Try the student attendance stats endpoint first (more comprehensive)
        const attendanceResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/student-attendance-stats/${studentId}`);
        attendanceData = attendanceResponse.data;
        console.log('Fetched attendance stats:', attendanceData);
      } catch (attendanceError) {
        console.warn('Student attendance stats endpoint failed:', attendanceError.message);
        // Try alternative attendance endpoint
        try {
          const altAttendanceResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/attendance/${studentId}`);
          attendanceData = altAttendanceResponse.data;
          console.log('Fetched attendance records:', attendanceData);
        } catch (altError) {
          console.warn('Alternative attendance endpoint also failed:', altError.message);
          // Try class-based attendance lookup
          try {
            const classAttendanceResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/class-attendance/${student.class}`);
            const classAttendanceData = classAttendanceResponse.data;
            // Filter for this specific student
            const studentAttendanceRecords = classAttendanceData.filter(record => record.studentId === studentId);
            if (studentAttendanceRecords.length > 0) {
              attendanceData = studentAttendanceRecords;
              console.log('Fetched class-based attendance:', attendanceData);
            }
          } catch (classError) {
            console.warn('Class attendance endpoint also failed:', classError.message);
          }
        }
      }
      
      // Handle different response structures
      let marksheetData;
      if (marksheetResponse.data.marksheet) {
        // Complete endpoint response
        marksheetData = marksheetResponse.data.marksheet;
      } else {
        // Basic endpoint response
        marksheetData = marksheetResponse.data;
      }
      
      setMarksheetData({
        ...marksheetData,
        attendanceData: attendanceData
      });
      setTemplate(templateResponse.data);
      setSelectedStudent(student);
      
    } catch (error) {
      console.error('Error fetching marksheet data:', error);
      if (error.response?.status === 404) {
        setError('No marksheet data found for this student. Please ensure marksheet data has been entered for this student.');
      } else {
        setError('Failed to fetch marksheet data. Please try again or contact administrator.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePatternSubmit = (e) => {
    e.preventDefault();
    if (patternLock === TEACHER_PATTERN) {
      setIsUnlocked(true);
      setError('');
    } else {
      setError('Invalid pattern. Access denied.');
      setPatternLock('');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const printBackSide = async (studentId) => {
    try {
      setLoading(true);
      console.log('Printing back side for student ID:', studentId);
      
      // Fetch student data directly from server to avoid cached data issues
      let student;
      try {
        const studentResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/${studentId}`);
        student = studentResponse.data;
        console.log('Fetched student data from server:', student.name, 'Class:', student.class);
      } catch (studentError) {
        console.error('Failed to fetch student from server, trying cached data:', studentError.message);
        // Fallback to cached data if server fetch fails
        student = students.find(s => s._id === studentId);
      }
      
      if (!student) {
        setError('Student not found');
        return;
      }

      // Validate that student belongs to selected class (if class is selected)
      if (selectedClass && student.class !== selectedClass) {
        console.warn(`Class mismatch: Expected ${selectedClass}, got ${student.class}`);
        setError(`Student class mismatch detected. Expected ${selectedClass}, but student is in ${student.class}`);
        return;
      }

      // Try to get ID card data for more complete information
      let idCardData = null;
      try {
        const idCardResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/idcard/${studentId}`);
        idCardData = idCardResponse.data;
        console.log('Fetched ID card data for student:', idCardData.name || 'No name in ID card');
        
        // Validate ID card data matches student
        if (idCardData && idCardData.name && idCardData.name !== student.name) {
          console.warn(`ID card name mismatch: Student name is ${student.name}, ID card name is ${idCardData.name}`);
        }
      } catch (idError) {
        console.log('ID card data not found, using student data only:', idError.message);
      }

      // Set student data and ID card data
      setSelectedStudent(student);
      setMarksheetData({ idCardData });
      setShowMarksheet(false);
      setShowBackSide(true);
      
      console.log('Back side data set for:', student.name, 'ID card name:', idCardData?.name || 'No ID card');
      
      // Auto-print after a short delay
      setTimeout(() => {
        window.print();
      }, 500);
      
    } catch (error) {
      console.error('Error loading student data for back side:', error);
      setError('Failed to load student data for back side');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (examData, subjects) => {
    if (!examData || !subjects) return 0;
    return subjects.reduce((total, subject) => {
      const mark = examData[subject.name] || 0;
      return total + (typeof mark === 'number' ? mark : 0);
    }, 0);
  };

  const calculateGrandTotal = (marksheetData, template) => {
    if (!marksheetData?.exams) return 0;
    
    let grandTotal = 0;
    const examStructure = template.examStructure || {};
    
    Object.keys(examStructure).forEach(examType => {
      if (examStructure[examType].enabled && marksheetData.exams[examType]) {
        grandTotal += calculateTotal(marksheetData.exams[examType], getEffectiveSubjects());
      }
    });
    
    return grandTotal;
  };

  const calculatePercentage = (total, maxMarks) => {
    if (!maxMarks || maxMarks === 0) return 0;
    return ((total / maxMarks) * 100).toFixed(2);
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C+';
    if (percentage >= 40) return 'C';
    if (percentage >= 33) return 'D';
    return 'F';
  };

  const getActualMaxMarks = (examType, subjectName) => {
    // For early childhood classes, use subject-specific maximum marks
    const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
    
    if (isEarlyChildhood) {
      // Define subject-specific maximum marks for early childhood
      const subjectKey = subjectName.toLowerCase().replace(/[^a-z]/g, '');
      
      if (subjectKey === 'gk' || subjectKey === 'evs' || 
          subjectKey === 'generalknowledge' || subjectKey === 'environmentalstudies') {
        return 50;
      }
      
      // For English, Hindi, Math in early childhood, use 100
      if (subjectKey === 'english' || subjectKey === 'hindi' || 
          subjectKey === 'mathematics' || subjectKey === 'math' || subjectKey === 'maths') {
        return 100;
      }
      
      // Default for any other early childhood subject
      return 50;
    } else {
      // For Class 1 and above, always use template maxMarks for consistency
      return template.examStructure[examType].maxMarks;
    }
  };

  const getExamActualMaxMarks = (examType) => {
    const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
    
    if (isEarlyChildhood) {
      // For early childhood, calculate based on subject-specific max marks
      // Only use subjects that are actually in the curriculum
      const earlyChildhoodSubjects = [
        { name: 'english', maxMarks: 100 },
        { name: 'hindi', maxMarks: 100 },
        { name: 'mathematics', maxMarks: 100 },
        { name: 'gk', maxMarks: 50 },
        { name: 'evs', maxMarks: 50 }
      ];
      
      return earlyChildhoodSubjects.reduce((total, subject) => {
        return total + subject.maxMarks;
      }, 0);
    } else {
      // For Class 1 and above, use consistent template-based calculation
      return template.examStructure[examType].maxMarks * (getEffectiveSubjects().length || 1);
    }
  };

  const getExamDisplayMarks = (examType) => {
    const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
    
    if (isEarlyChildhood) {
      // For early childhood, show range of subject-specific marks
      const marksUsed = getEffectiveSubjects().map(subject => 
        getActualMaxMarks(examType, subject.name)
      );
      
      const uniqueMarks = [...new Set(marksUsed)].sort((a, b) => a - b);
      
      if (uniqueMarks.length === 1) {
        return `${uniqueMarks[0]}`;
      } else {
        return `${Math.min(...uniqueMarks)}-${Math.max(...uniqueMarks)}`;
      }
    } else {
      // For Class 1 and above, show per-subject marks from template
      return template.examStructure[examType].maxMarks;
    }
  };

  const getDefaultSubjects = (studentClass) => {
    // Define default subjects for different classes
    const commonSubjects = [
      { name: 'english', displayName: 'English' },
      { name: 'hindi', displayName: 'Hindi' },
      { name: 'mathematics', displayName: 'Mathematics' },
      { name: 'science', displayName: 'Science' },
      { name: 'socialScience', displayName: 'Social Science' },
      { name: 'drawing', displayName: 'Drawing' },
      { name: 'computer', displayName: 'Computer' },
      { name: 'gk', displayName: 'G.K.' },
      { name: 'moralScience', displayName: 'Moral Science' }
    ];

    const earlyChildhoodSubjects = [
      { name: 'english', displayName: 'English' },
      { name: 'hindi', displayName: 'Hindi' },
      { name: 'mathematics', displayName: 'Mathematics' },
      { name: 'drawing', displayName: 'Drawing' },
      { name: 'gk', displayName: 'G.K.' },
      { name: 'evs', displayName: 'EVS' }
    ];

    // Return appropriate subjects based on class
    if (['Nursery', 'LKG', 'UKG', 'HKG'].includes(studentClass)) {
      return earlyChildhoodSubjects;
    }
    return commonSubjects;
  };

  const getEffectiveSubjects = () => {
    // Use template subjects if available, otherwise use default subjects
    if (template?.subjects && template.subjects.length > 0) {
      return template.subjects;
    }
    return getDefaultSubjects(selectedStudent?.class);
  };

  const calculateAttendancePercentage = () => {
    if (!marksheetData?.attendanceData) {
      return 'N/A'; // Show N/A instead of hardcoded 95%
    }

    const attendanceData = marksheetData.attendanceData;
    
    // If attendance data is an array of records (from AttendanceRecord collection)
    if (Array.isArray(attendanceData)) {
      const totalDays = attendanceData.length;
      const presentDays = attendanceData.filter(record => 
        record.status === 'present' || record.status === 'P'
      ).length;
      
      if (totalDays === 0) return 'N/A';
      const percentage = ((presentDays / totalDays) * 100).toFixed(1);
      return `${percentage}%`;
    }
    
    // If attendance data has stats object (from student-attendance-stats endpoint)
    if (attendanceData.stats) {
      const { presentDays, totalMarked } = attendanceData.stats;
      if (totalMarked > 0) {
        const percentage = ((presentDays / totalMarked) * 100).toFixed(1);
        return `${percentage}%`;
      }
    }
    
    // If attendance data has totalDays and presentDays properties
    if (attendanceData.totalDays && attendanceData.presentDays) {
      const percentage = ((attendanceData.presentDays / attendanceData.totalDays) * 100).toFixed(1);
      return `${percentage}%`;
    }
    
    // If attendance data has summary with total and present counts
    if (attendanceData.summary) {
      const { totalWorkingDays, totalPresent } = attendanceData.summary;
      if (totalWorkingDays && totalPresent !== undefined) {
        const percentage = ((totalPresent / totalWorkingDays) * 100).toFixed(1);
        return `${percentage}%`;
      }
    }
    
    // Fallback - show N/A instead of hardcoded percentage
    return 'N/A';
  };

  const getSortedExams = () => {
    const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
    
    if (isEarlyChildhood) {
      // For early childhood, get all enabled exams and sort them properly
      // Limit to maximum 3 exams for early childhood
      const allExams = Object.keys(template.examStructure || {})
        .filter(exam => template.examStructure[exam].enabled)
        .sort((a, b) => {
          // Define preferred order for early childhood exams
          const getExamPriority = (examName) => {
            const name = examName.toLowerCase();
            if (name.includes('quarter') || name.includes('term1')) return 1;
            if (name.includes('half') || name.includes('mid')) return 2;
            if (name.includes('annual') || name.includes('final')) return 3;
            if (name.includes('term2')) return 4;
            if (name.includes('term3')) return 5;
            if (name.includes('project')) return 6;
            return template.examStructure[examName].order || 999;
          };
          
          return getExamPriority(a) - getExamPriority(b);
        });
      
      // For early childhood, only take the first 3 exams to ensure 1200 max marks
      return allExams.slice(0, 3);
    } else {
      // For other classes: use all enabled exams
      return Object.keys(template.examStructure || {})
        .filter(exam => template.examStructure[exam].enabled)
        .sort((a, b) => {
          return template.examStructure[a].order - template.examStructure[b].order;
        });
    }
  };

  // Access control check
  if (!['teacher', 'admin1', 'admin', 'principal', 'teacher_admin_level1'].includes(user?.role)) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <h2>🔒 Access Restricted</h2>
          <p>This printable marksheet feature is only available to teachers and administrators.</p>
          <p>Students cannot access or print marksheets.</p>
        </div>
      </div>
    );
  }

  // Pattern lock screen
  if (!isUnlocked) {
    return (
      <div className="pattern-lock-screen">
        <div className="pattern-lock-content">
          <div className="lock-icon">🔐</div>
          <h2>Printable Final Marksheet</h2>
          <p>Enter teacher pattern to access printable marksheets</p>
          
          <form onSubmit={handlePatternSubmit} className="pattern-form">
            <input
              type="password"
              value={patternLock}
              onChange={(e) => setPatternLock(e.target.value)}
              placeholder="Enter pattern code"
              className="pattern-input"
              maxLength="4"
            />
            <button type="submit" className="unlock-btn">
              Unlock Access
            </button>
          </form>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="security-note">
            <small>🛡️ This is a secure area for teachers only</small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="printable-marksheet-container">
      {!showMarksheet && !showBackSide ? (
        <div className="student-selection">
          <div className="selection-header">
            <h2>📋 Printable Final Marksheet</h2>
            <p>Select a class and then choose a student to generate their complete marksheet</p>
          </div>

          {loading && <div className="loading">Loading students...</div>}
          {error && <div className="error-message">{error}</div>}

          {/* Class Selection Dropdown */}
          <div className="class-filter">
            <select 
              className="class-select"
              value={selectedClass}
              onChange={(e) => handleClassChange(e.target.value)}
            >
              <option value="">All Classes</option>
              {[...new Set(students.map(student => student.class))]
                .sort((a, b) => {
                  const getClassOrder = (className) => {
                    if (className === 'LKG') return 0;
                    if (className === 'UKG') return 1;
                    const num = parseInt(className);
                    return isNaN(num) ? 999 : num + 2;
                  };
                  return getClassOrder(a) - getClassOrder(b);
                })
                .map(className => (
                  <option key={className} value={className}>
                    Class {className}
                  </option>
                ))
              }
            </select>
          </div>

          {/* Students Grid */}
          <div className="students-grid">
            {filteredStudents.map(student => (
              <div 
                key={student._id} 
                className="student-card"
                onClick={() => {
                  fetchMarksheetData(student._id);
                  setShowMarksheet(true);
                }}
              >
                <div className="student-info">
                  <h3>{student.name}</h3>
                  <p>Class: {student.class}</p>
                  <p>ID: {student.studentId || student._id}</p>
                </div>
                <div className="generate-btn">Generate Marksheet</div>
                <div className="print-back-btn" onClick={(e) => {
                  e.stopPropagation();
                  printBackSide(student._id);
                }}>Print Back Side</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="marksheet-view">
          <div className="print-controls no-print">
            <button onClick={() => setShowMarksheet(false)} className="back-btn">
              ← Back to Students
            </button>
            <button onClick={handlePrint} className="print-btn">
              🖨️ Print Marksheet
            </button>
          </div>

          {loading && <div className="loading">Loading marksheet...</div>}
          {error && <div className="error-message">{error}</div>}

          {showBackSide && selectedStudent ? (
            <div className="marksheet-back-side">
              <div className="back-side-container">
                <div className="back-left-panel">
              
                  
                  <div className="reference-section">
                    <h5>Grade Scale</h5>
                    <table className="reference-table">
                      <tr><td>A+</td><td>90-100%</td><td>Outstanding</td></tr>
                      <tr><td>A</td><td>80-89%</td><td>Excellent</td></tr>
                      <tr><td>B+</td><td>70-79%</td><td>Very Good</td></tr>
                      <tr><td>B</td><td>60-69%</td><td>Good</td></tr>
                      <tr><td>C+</td><td>50-59%</td><td>Above Average</td></tr>
                      <tr><td>C</td><td>40-49%</td><td>Average</td></tr>
                      <tr><td>D</td><td>33-39%</td><td>Below Average</td></tr>
                      <tr><td>F</td><td>Below 33%</td><td>Fail</td></tr>
                    </table>
                  </div>

                  <div className="reference-section">
                    <h5>Division Criteria</h5>
                    <table className="reference-table">
                      <tr><td>First Division</td><td>60% & Above</td></tr>
                      <tr><td>Second Division</td><td>45% - 59%</td></tr>
                      <tr><td>Third Division</td><td>33% - 44%</td></tr>
                      <tr><td>Fail</td><td>Below 33%</td></tr>
                    </table>
                  </div>

                  <div className="reference-section">
                    <h4>Pass/Fail Criteria</h4>
                    <ul className="criteria-list">
                      <li>Minimum 33% in each subject</li>
                      <li>Minimum 33% overall aggregate</li>
                      <li>Regular attendance required</li>
                      <li>Completion of all assignments</li>
                    </ul>
                  </div>

                  <div className="reference-section">
                    <h4>Performance Remarks</h4>
                    <table className="reference-table">
                      <tr><td>Outstanding</td><td>90%+</td></tr>
                      <tr><td>Excellent</td><td>80-89%</td></tr>
                      <tr><td>Very Good</td><td>70-79%</td></tr>
                      <tr><td>Good</td><td>60-69%</td></tr>
                      <tr><td>Satisfactory</td><td>50-59%</td></tr>
                      <tr><td>Needs Improvement</td><td>33-49%</td></tr>
                    </table>
                  </div>
                </div>

                <div className="back-right-panel">
                  <div className="school-branding">
                    <img src="/logo.png" alt="School Logo" className="back-logo" />
                    <h1>The Rising School</h1>
                   
                  </div>

                  <div className="student-info-form">
                    <h3>Student Information</h3>
                    
                    <div className="info-grid">
                      <div className="info-group">
                        <label>Student Name:</label>
                        <div className="info-value">
                          {/* Always prioritize selectedStudent data over ID card data for name */}
                          {selectedStudent?.name || marksheetData?.idCardData?.name || 'N/A'}
                        </div>
                      </div>

                      <div className="info-group">
                        <label>Father's Name:</label>
                        <div className="info-value">
                          {/* Use ID card data first, then selectedStudent data */}
                          {marksheetData?.idCardData?.fathersName || selectedStudent?.fathersName || 'N/A'}
                        </div>
                      </div>

                      <div className="info-group">
                        <label>Mother's Name:</label>
                        <div className="info-value">
                          {marksheetData?.idCardData?.mothersName || selectedStudent?.mothersName || 'N/A'}
                        </div>
                      </div>

                      <div className="info-group">
                        <label>Class:</label>
                        <div className="info-value">
                          {/* Always use selectedStudent class to ensure consistency */}
                          {selectedStudent?.class || marksheetData?.idCardData?.class || 'N/A'}
                        </div>
                      </div>

                      <div className="info-group">
                        <label>Section:</label>
                        <div className="info-value">
                          {marksheetData?.idCardData?.section || selectedStudent?.section || 'A'}
                        </div>
                      </div>

                      <div className="info-group">
                        <label>Scholar No:</label>
                        <div className="info-value">
                          {marksheetData?.idCardData?.scholarNo || marksheetData?.idCardData?.studentId || selectedStudent?.studentId || 'N/A'}
                        </div>
                      </div>

                      <div className="info-group">
                        <label>Date of Birth:</label>
                        <div className="info-value">
                          {marksheetData?.idCardData?.dateOfBirth || marksheetData?.idCardData?.dob || selectedStudent?.dateOfBirth || 'N/A'}
                        </div>
                      </div>

                      <div className="info-group">
                        <label>Mobile:</label>
                        <div className="info-value">
                          {marksheetData?.idCardData?.mobile || selectedStudent?.phone || selectedStudent?.contactNumber || 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div className="info-group full-width">
                      <label>Address:</label>
                      <div className="info-value">{marksheetData?.idCardData?.address || selectedStudent.address || 'N/A'}</div>
                    </div>

                    <div className="info-group full-width">
                      <label>Academic Year:</label>
                      <div className="info-value">{marksheetData?.academicYear || '2026-2027'}</div>
                    </div>
                  </div>

                  <div className="school-contact">
                
                    <p> Sector 12 Savina Udaipur - 313002</p>
                    <p>🌐 www.therisingschool.com</p>
                
                  </div>
                </div>
              </div>
            </div>
          ) : marksheetData && template && selectedStudent && (
            <div className="marksheet-content">
              {/* School Header */}
              <div className="school-header">
                <div className="logo-section">
                  <img src="/logo.png" alt="School Logo" className="school-logo" />
                </div>
                <div className="school-info">
                  <h1>The Rising School</h1>
                  <p className="school-address">26, 27, 28 Mursheed Nagar Savina Udaipur - PIN - 313002<br />www.therisingschool.com</p>
                  <h2>FINAL MARKSHEET</h2>
                </div>
                <div className="qr-section">
                  <div className="qr-code">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent('https://www.therisingschool.com')}`}
                      alt="School Website QR Code" 
                      className="qr-image" 
                    />
                    <p className="qr-label">Scan for Website</p>
                  </div>
                </div>
              </div>

              {/* Student Information */}
              <div className="student-details">
                <div className="detail-row">
                  <div className="detail-item">
                    <strong>Student Name:</strong> {selectedStudent.name}
                  </div>
                  <div className="detail-item">
                    <strong>Father's Name:</strong> {selectedStudent.fathersName || 'N/A'}
                  </div>
                  <div className="detail-item">
                    <strong>Class:</strong> {selectedStudent.class}
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-item">
                    <strong>Student ID:</strong> {selectedStudent.studentId || selectedStudent._id}
                  </div>
                  <div className="detail-item">
                    <strong>Section:</strong> {selectedStudent.section || 'A'}
                  </div>
                  <div className="detail-item">
                    <strong>Academic Year:</strong> {marksheetData?.academicYear || '2026-2027'}
                  </div>
                </div>
              </div>

              {/* Marks Table */}
              <div className="marks-table-container">
                <table className="marks-table">
                  <thead>
                    <tr>
                      <th rowSpan="2" className="subject-header">SUBJECTS</th>
                      {getSortedExams().map(examType => (
                          <th key={examType} className="exam-header">
                            {template.examStructure[examType].displayName || examType}
                            <br />
                            <small>(
                              {(() => {
                                const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                                if (isEarlyChildhood) {
                                  return "50-100 marks";
                                } else {
                                  return `${template.examStructure[examType].maxMarks} marks`;
                                }
                              })()} 
                            )</small>
                          </th>
                        ))}
                      <th rowSpan="2" className="total-header">TOTAL</th>
                      <th rowSpan="2" className="grade-header">GRADE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getEffectiveSubjects().map(subject => {
                      const subjectTotal = Object.keys(template.examStructure || {})
                        .filter(exam => template.examStructure[exam].enabled)
                        .reduce((total, examType) => {
                          const mark = marksheetData.exams?.[examType]?.[subject.name] || 0;
                          return total + (typeof mark === 'number' ? mark : 0);
                        }, 0);
                      
                      const maxMarks = Object.keys(template.examStructure || {})
                        .filter(exam => template.examStructure[exam].enabled)
                        .reduce((total, examType) => total + getActualMaxMarks(examType, subject.name), 0);
                      
                      const percentage = calculatePercentage(subjectTotal, maxMarks);
                      
                      return (
                        <tr key={subject.name}>
                          <td className="subject-name">{subject.displayName}</td>
                          {getSortedExams().map(examType => (
                              <td key={examType} className="mark-cell">
                                {(() => {
                                  const mark = marksheetData.exams?.[examType]?.[subject.name];
                                  if (mark === 0) return '0';
                                  if (mark === null || mark === undefined || mark === '') return '-';
                                  return mark;
                                })()}
                              </td>
                            ))}
                          <td className="total-cell">{subjectTotal}</td>
                          <td className="grade-cell">{getGrade(percentage)}</td>
                        </tr>
                      );
                    })}
                    
                    {/* Grand Total Row */}
                    <tr className="grand-total-row">
                      <td className="subject-name"><strong>GRAND TOTAL</strong></td>
                      {getSortedExams().map(examType => {
                          const examTotal = getEffectiveSubjects().reduce((total, subject) => {
                            const mark = marksheetData.exams?.[examType]?.[subject.name] || 0;
                            return total + (typeof mark === 'number' ? mark : 0);
                          }, 0);
                          
                          const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                          const examMaxMarks = isEarlyChildhood ? 400 : getExamActualMaxMarks(examType);
                          const examPercentage = calculatePercentage(examTotal, examMaxMarks);
                          
                          return (
                            <td key={examType} className="total-cell">
                              <strong>{examTotal}</strong>
                              <br />
                              <small>({examPercentage}%)</small>
                            </td>
                          );
                        })}
                      <td className="total-cell">
                        <strong>{calculateGrandTotal(marksheetData, template)}</strong>
                        <br />
                        <small>(
                          {(() => {
                            const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                            
                            if (isEarlyChildhood) {
                              // For early childhood: use fixed 1200 maximum marks
                              const grandTotal = calculateGrandTotal(marksheetData, template);
                              return calculatePercentage(grandTotal, 1200);
                            } else {
                              // For other classes: use dynamic calculation
                              const totalMaxMarks = getSortedExams().reduce((total, examType) => 
                                total + getExamActualMaxMarks(examType), 0);
                              const grandTotal = calculateGrandTotal(marksheetData, template);
                              return calculatePercentage(grandTotal, totalMaxMarks);
                            }
                          })()}%
                        )</small>
                      </td>
                      <td className="grade-cell">
                        <strong>
                          {(() => {
                            const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                            const grandTotal = calculateGrandTotal(marksheetData, template);
                            
                            let overallPercentage;
                            if (isEarlyChildhood) {
                              overallPercentage = calculatePercentage(grandTotal, 1200);
                            } else {
                              const totalMaxMarks = getSortedExams().reduce((total, examType) => 
                                total + getExamActualMaxMarks(examType), 0);
                              overallPercentage = calculatePercentage(grandTotal, totalMaxMarks);
                            }
                            return getGrade(overallPercentage);
                          })()}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Result Summary */}
              <div className="result-summary">
                <div className="summary-row">
                  <div className="summary-item">
                    <strong>Total Marks Obtained:</strong> {calculateGrandTotal(marksheetData, template)}
                  </div>
                  <div className="summary-item">
                    <strong>Maximum Marks:</strong> {(() => {
                      const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                      
                      if (isEarlyChildhood) {
                        // For early childhood: Fixed calculation
                        // Hindi, English, Math: 100 each × 3 exams = 900
                        // EVS, GK: 50 each × 3 exams = 300
                        // Total: 1200
                        return 1200;
                      } else {
                        // For other classes: use dynamic calculation
                        const totalMaxMarks = getSortedExams().reduce((total, examType) => 
                          total + getExamActualMaxMarks(examType), 0);
                        return totalMaxMarks;
                      }
                    })()}
                  </div>
                  <div className="summary-item">
                    <strong>Percentage:</strong> {(() => {
                      const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                      
                      if (isEarlyChildhood) {
                        // For early childhood: use fixed 1200 maximum marks
                        const grandTotal = calculateGrandTotal(marksheetData, template);
                        return calculatePercentage(grandTotal, 1200);
                      } else {
                        // For other classes: use dynamic calculation
                        const totalMaxMarks = getSortedExams().reduce((total, examType) => 
                          total + getExamActualMaxMarks(examType), 0);
                        const grandTotal = calculateGrandTotal(marksheetData, template);
                        return calculatePercentage(grandTotal, totalMaxMarks);
                      }
                    })()}%
                  </div>
                </div>
                <div className="summary-row">
                  <div className="summary-item">
                    <strong>Division:</strong> {(() => {
                      const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                      const grandTotal = calculateGrandTotal(marksheetData, template);
                      
                      let overallPercentage;
                      if (isEarlyChildhood) {
                        overallPercentage = calculatePercentage(grandTotal, 1200);
                      } else {
                        const totalMaxMarks = getSortedExams().reduce((total, examType) => 
                          total + getExamActualMaxMarks(examType), 0);
                        overallPercentage = calculatePercentage(grandTotal, totalMaxMarks);
                      }
                      
                      // Check if student failed in any subject (grade F)
                      const subjects = getEffectiveSubjects();
                      let hasFailedSubject = false;
                      
                      for (const subject of subjects) {
                        const subjectTotal = Object.keys(template.examStructure || {})
                          .filter(exam => template.examStructure[exam].enabled)
                          .reduce((total, examType) => {
                            const mark = marksheetData.exams?.[examType]?.[subject.name] || 0;
                            return total + (typeof mark === 'number' ? mark : 0);
                          }, 0);
                        
                        const maxMarks = Object.keys(template.examStructure || {})
                          .filter(exam => template.examStructure[exam].enabled)
                          .reduce((total, examType) => total + getActualMaxMarks(examType, subject.name), 0);
                        
                        const percentage = calculatePercentage(subjectTotal, maxMarks);
                        
                        if (percentage < 33) {
                          hasFailedSubject = true;
                          break;
                        }
                      }
                      
                      // If failed in any subject or overall < 33%, no division
                      if (hasFailedSubject || overallPercentage < 33) return 'Fail';
                      if (overallPercentage >= 60) return 'First Division';
                      if (overallPercentage >= 45) return 'Second Division';
                      return 'Third Division';
                    })()}
                  </div>
                  <div className="summary-item">
                    <strong>Result:</strong> {(() => {
                      const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                      const grandTotal = calculateGrandTotal(marksheetData, template);
                      
                      let overallPercentage;
                      if (isEarlyChildhood) {
                        overallPercentage = calculatePercentage(grandTotal, 1200);
                      } else {
                        const totalMaxMarks = getSortedExams().reduce((total, examType) => 
                          total + getExamActualMaxMarks(examType), 0);
                        overallPercentage = calculatePercentage(grandTotal, totalMaxMarks);
                      }
                      
                      // Check if student failed in any subject (grade F)
                      const subjects = getEffectiveSubjects();
                      let hasFailedSubject = false;
                      
                      for (const subject of subjects) {
                        const subjectTotal = Object.keys(template.examStructure || {})
                          .filter(exam => template.examStructure[exam].enabled)
                          .reduce((total, examType) => {
                            const mark = marksheetData.exams?.[examType]?.[subject.name] || 0;
                            return total + (typeof mark === 'number' ? mark : 0);
                          }, 0);
                        
                        const maxMarks = Object.keys(template.examStructure || {})
                          .filter(exam => template.examStructure[exam].enabled)
                          .reduce((total, examType) => total + getActualMaxMarks(examType, subject.name), 0);
                        
                        const percentage = calculatePercentage(subjectTotal, maxMarks);
                        
                        // If any subject has less than 33%, student fails
                        if (percentage < 33) {
                          hasFailedSubject = true;
                          break;
                        }
                      }
                      
                      // Student fails if overall percentage < 33% OR any subject < 33%
                      return (overallPercentage >= 33 && !hasFailedSubject) ? 'PASS' : 'FAIL';
                    })()}
                  </div>
                  <div className="summary-item">
                    <strong>Promoted to:</strong> {(() => {
                      const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                      const grandTotal = calculateGrandTotal(marksheetData, template);
                      
                      let overallPercentage;
                      if (isEarlyChildhood) {
                        overallPercentage = calculatePercentage(grandTotal, 1200);
                      } else {
                        const totalMaxMarks = getSortedExams().reduce((total, examType) => 
                          total + getExamActualMaxMarks(examType), 0);
                        overallPercentage = calculatePercentage(grandTotal, totalMaxMarks);
                      }
                      
                      // Check if student failed in any subject (grade F)
                      const subjects = getEffectiveSubjects();
                      let hasFailedSubject = false;
                      
                      for (const subject of subjects) {
                        const subjectTotal = Object.keys(template.examStructure || {})
                          .filter(exam => template.examStructure[exam].enabled)
                          .reduce((total, examType) => {
                            const mark = marksheetData.exams?.[examType]?.[subject.name] || 0;
                            return total + (typeof mark === 'number' ? mark : 0);
                          }, 0);
                        
                        const maxMarks = Object.keys(template.examStructure || {})
                          .filter(exam => template.examStructure[exam].enabled)
                          .reduce((total, examType) => total + getActualMaxMarks(examType, subject.name), 0);
                        
                        const percentage = calculatePercentage(subjectTotal, maxMarks);
                        
                        if (percentage < 33) {
                          hasFailedSubject = true;
                          break;
                        }
                      }
                      
                      // Only promote if passed overall AND no subject failures
                      if (overallPercentage >= 33 && !hasFailedSubject) {
                        const currentClass = selectedStudent.class;
                        const classNumber = parseInt(currentClass.replace(/[^0-9]/g, ''));
                        return classNumber ? `Class ${classNumber + 1}` : 'Next Class';
                      }
                      return 'Same Class';
                    })()}
                  </div>
                </div>
                <div className="summary-row">
                  <div className="summary-item">
                    <strong>Attendance:</strong> {calculateAttendancePercentage()}
                  </div>
                  <div className="summary-item">
                    <strong>Drawing:</strong> {(() => {
                      // Get drawing grade from marksheet data
                      const drawingGrade = marksheetData?.grades?.drawing || 
                                          marksheetData?.drawing || 
                                          marksheetData?.exams?.drawing || 
                                          '_______';
                      return drawingGrade;
                    })()}
                  </div>
                </div>
                <div className="summary-row">
                  <div className="summary-item full-width">
                    <strong>Remarks:</strong> {(() => {
                      const isEarlyChildhood = ['Nursery', 'LKG', 'UKG', 'HKG'].includes(selectedStudent?.class);
                      const grandTotal = calculateGrandTotal(marksheetData, template);
                      
                      let overallPercentage;
                      if (isEarlyChildhood) {
                        overallPercentage = calculatePercentage(grandTotal, 1200);
                      } else {
                        const totalMaxMarks = getSortedExams().reduce((total, examType) => 
                          total + getExamActualMaxMarks(examType), 0);
                        overallPercentage = calculatePercentage(grandTotal, totalMaxMarks);
                      }
                      
                      // Check if student failed in any subject (grade F)
                      const subjects = getEffectiveSubjects();
                      let hasFailedSubject = false;
                      let failedSubjects = [];
                      
                      for (const subject of subjects) {
                        const subjectTotal = Object.keys(template.examStructure || {})
                          .filter(exam => template.examStructure[exam].enabled)
                          .reduce((total, examType) => {
                            const mark = marksheetData.exams?.[examType]?.[subject.name] || 0;
                            return total + (typeof mark === 'number' ? mark : 0);
                          }, 0);
                        
                        const maxMarks = Object.keys(template.examStructure || {})
                          .filter(exam => template.examStructure[exam].enabled)
                          .reduce((total, examType) => total + getActualMaxMarks(examType, subject.name), 0);
                        
                        const percentage = calculatePercentage(subjectTotal, maxMarks);
                        
                        if (percentage < 33) {
                          hasFailedSubject = true;
                          failedSubjects.push(subject.displayName);
                        }
                      }
                      
                      // If failed in any subject, show failure remarks
                      if (hasFailedSubject) {
                        if (failedSubjects.length === 1) {
                          return `Failed in ${failedSubjects[0]}`;
                        } else if (failedSubjects.length <= 3) {
                          return `Failed in ${failedSubjects.join(', ')}`;
                        } else {
                          return `Failed in ${failedSubjects.length} subjects`;
                        }
                      }
                      
                      // If overall percentage is low but no individual subject failures
                      if (overallPercentage < 33) {
                        return 'Overall Performance Below Standard';
                      }
                      
                      // Success remarks based on overall percentage
                      if (overallPercentage >= 90) return 'Outstanding Performance';
                      if (overallPercentage >= 80) return 'Excellent Work';
                      if (overallPercentage >= 70) return 'Very Good';
                      if (overallPercentage >= 60) return 'Good Performance';
                      if (overallPercentage >= 50) return 'Satisfactory';
                      return 'Needs Improvement';
                    })()}
                  </div>
                </div>
              </div>

              {/* Signatures */}
              <div className="signatures">
                <div className="signature-item">
                  <div className="signature-line"></div>
                  <p>Parent</p>
                </div>
                <div className="signature-item">
                  <div className="signature-line"></div>
                  <p>Exam Incharge</p>
                </div>
                <div className="signature-item">
                  <div className="signature-line"></div>
                  <p>Class Teacher</p>
                </div>
                <div className="signature-item">
                  <div className="signature-line"></div>
                  <p>Principal</p>
                </div>
                <div className="signature-item">
                  <div className="signature-line"></div>
                  <p>Director</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PrintableMarksheet;